import { getTeaLocale } from '@home/utils/lang';
import CardCommon from '@op/components/CardCommon';
import FilterBoxCommon from '@op/components/FilterBoxCommon';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ConfigProvider, Table } from 'tea-component';
import { getLocale, useIntl } from 'umi';
import NIKKEAnalysis from './NIKKEAnalysis';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import useNIKKETableConfig from '@op/components/TableCommonHook/columns/NIKKE';
import { NO_DATE } from '@op/constants';
import { useNikkeTableTopicLevelTwoList } from '@op/pages/Summary/hooks/useTopicConfig';
import UserInfoItem from '@op/components/Snippets/UserInfoItem';
import { HeroMapType } from '@op/pages/Hero/utils';
import { LanguageType, LocaleType } from '@op/types/common';
import { useTopicLevelTwoTimeline } from '@op/pages/Summary/hooks/useTimelineData';
import { dealwithNIKKEList } from '../utils';
import useTableSelect, { TableColorType } from '@op/hooks/useTableSelect';
import { downloadAction } from '@op/pages/download/utils';

const { expandable, pageable, sortable, scrollable, autotip } = Table.addons;

type ChildrenParamsType = {
  transHeroInfo: (name: string) => {
    icon: string;
    name: string;
  };
  filter: any;
  selectedKeysMap: TableColorType;
  loading: boolean;
};
export default function NIKKETable({
  children,
  wholeGameInfo,
  filter,
  heroInfoMap,
}: {
  children: (item: ChildrenParamsType) => any;
  wholeGameInfo: any;
  filter: any;
  heroInfoMap: HeroMapType;
}) {
  const [languageType, setLanguageType] = useState<LanguageType>('origin');
  const lang: LocaleType = getLocale();
  const {
    clearAllSelectInfo,
    saveDefaultData,
    selectKeys,
    selectedKeysMap,
    commonIndexColumns,
  } = useTableSelect({
    tableKey: 'topic',
    allSelectCount: 20,
    maxCount: 20,
    defaultSelect: 10,
  });

  const {
    records,
    pageInfo,
    setPageInfo,
    total,
    sort,
    setSort,
    loading,
    getRequestParams,
  } = useNikkeTableTopicLevelTwoList({
    filter,
    dealwithTableData: dealwithNIKKEList,
    defaultPageInfo: {
      page: 1,
      page_size: 20,
    },
    otherMetrics: ['views', 'neutral_mentions'],
    saveDefaultData,
    clearAllSelectInfo,
  });

  function transHeroInfo(name: string) {
    const { icon, ...nameMap } = heroInfoMap[name] || {};

    return {
      icon,
      name: nameMap?.[lang],
    };
  }

  const childrenFilter = useMemo(() => {
    return { ...filter, topic: selectKeys };
  }, [selectKeys]);

  return (
    <div className="dbo-hero-tablecard">
      {children({
        transHeroInfo,
        filter: childrenFilter,
        selectedKeysMap,
        loading,
      })}
      <FilterBoxCommon
        left={[{ key: 'language', defaultValue: 'origin' }]}
        right={[
          {
            key: 'download',
            event: {
              onClick: () => {
                buriedBtnPoint('DownloadTable');
                downloadAction({
                  key: 'NikkeTable',
                  fileName: 'Nikke Table',
                  filter: getRequestParams(),
                  auxiliaryParams: {
                    heroInfoMap,
                    sort,
                  },
                });
              },
            },
          },
        ]}
        onChange={(value, key) => {
          key === 'language' && setLanguageType(value[key]);
        }}
      ></FilterBoxCommon>
      <CardCommon loading={loading} loadingHeight={360}>
        <TableCommon
          {...{
            languageType,
            total,
            filter,
            wholeGameInfo,
            sort,
            setSort,
            setPageInfo,
            heroInfoMap,
            pageInfo,
            commonIndexColumns,
            records,
          }}
        ></TableCommon>
      </CardCommon>
    </div>
  );
}

function TableCommon({
  languageType,
  total,
  filter,
  wholeGameInfo,
  sort,
  setSort,
  setPageInfo,
  heroInfoMap,
  pageInfo,
  commonIndexColumns,
  records,
}: any) {
  const timelineFilter = useMemo(() => {
    const topic = records.map((item: any) => item.topic);
    return {
      ...filter,
      topic,
    };
  }, [records]);

  // 批量请求图表数据
  const {
    timelineMap,
    loading: chartLoading,
    apiError: chartApiError,
  } = useTopicLevelTwoTimeline({
    filter: timelineFilter,
    topicLevel: 'L2',
  });

  const { columns } = useNIKKETableConfig({
    timelineMap,
    filter,
    sort: sort,
    setSort: setSort,
    setPageInfo: setPageInfo,
    heroInfoMap,
  });

  const { formatMessage } = useIntl();
  // 当前展开的产品
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const stopRef = useRef(false);

  const getTableAddons = useCallback(() => {
    const addons = [
      scrollable({ minWidth: 1500 }),
      autotip({ emptyText: formatMessage({ id: NO_DATE.SELECTION }) }),
      expandable({
        expandedKeys,
        targetColumnKey: 'Index',
        onExpandedKeysChange: (keys, { operateKey, operateType }) => {
          if (stopRef.current) return;
          if (operateType === 'expand') {
            buriedBtnPoint('Open', operateKey);
          }
          setExpandedKeys(keys);
        },
        rowExpand: true,
        render(record) {
          return (
            <NIKKEAnalysis
              hero={record.topic}
              filter={filter}
              language={languageType}
              wholeGameInfo={wholeGameInfo}
            />
          );
        },
      }),
    ];

    if (total > 10) {
      let pageableParams: any = { pageSize: 20 };
      if (pageInfo.page_size !== 1000) {
        pageableParams = {
          recordCount: total,
          pageIndex: pageInfo.page,
          pageSize: pageInfo.page_size,
          onPagingChange: (query: any) => {
            const localPageInfo = {
              page: query.pageIndex || 1,
              page_size: query.pageSize || 10,
            };
            setPageInfo({ ...localPageInfo });
          },
        };
      }
      addons.push(pageable(pageableParams));
    }
    return addons;
  }, [languageType, pageInfo, expandedKeys]);

  const lang: LocaleType = getLocale();
  const locale = getTeaLocale(lang);

  return (
    <ConfigProvider locale={locale}>
      <Table
        className="dbo-topic-tracking-table dbo-topic-tracking-table--nikke"
        bordered="all"
        records={records}
        recordKey={'topic'}
        columns={[
          commonIndexColumns({
            headerInfo: (
              <>
                <span className="dbo-table-th-index">#</span>
                {formatMessage({ id: 'Nikke' })}
              </>
            ),
            pageInfo,
            dealwithItemElement: ({ record, defaultIndex }:any) => {
              const key = record.topic;
              const { icon, ...nameMap } = heroInfoMap[key] || {};

              return (
                <>
                  <span className="dbo-table-td-index">{defaultIndex}</span>
                  <UserInfoItem
                    name={nameMap['zh-CN']}
                    children={(className) => (
                      <span className={`${className} herotable-subname`}>
                        {nameMap['en-US']}
                      </span>
                    )}
                    onClick={() => {
                      if (expandedKeys.includes(key)) {
                        setExpandedKeys(
                          expandedKeys.filter((item) => item !== key),
                        );
                      } else {
                        setExpandedKeys([...expandedKeys, key]);
                      }
                    }}
                    icon={icon}
                  />
                </>
              );
            },
            width: 320,
            tips: 'Select Top 20 items',
          }),
          ...columns,
        ]}
        addons={getTableAddons()}
      />
    </ConfigProvider>
  );
}
